'use client';

import { type FC, useMemo, useState } from 'react';
import { CheckIcon, ChevronsUpDownIcon, Loader2Icon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@r/components/ui/button.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@r/components/ui/command.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';
import { cn } from '@r/utils/utils.ts';

export type Option = {
  id: number | string;
  name: string;
};

type SearchableSelectTagInputProps = {
  value: Option[];
  onChange: (newTags: Option[]) => void;
  options: Option[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  maxVisibleOptions?: number;
  getOverflowMessage?: (hiddenOptionsCount: number) => string;
};

const SearchableSelectTagInput: FC<SearchableSelectTagInputProps> = ({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  loading = false,
  maxVisibleOptions = 100,
  getOverflowMessage
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const filteredOptions = useMemo(
    () => options.filter((option) => !value.some((selected) => selected.id === option.id)),
    [options, value]
  );

  const handleSelect = (option: Option) => {
    onChange([...value, option]);
    setOpen(false);
  };

  const handleRemoveTag = (optionId: number | string) => {
    onChange(value.filter((option) => option.id !== optionId));
  };

  const placeholderText = placeholder ? t(placeholder) : t('admin.kpi.filter.select.tag.label');
  const resolvedSearchPlaceholder =
    searchPlaceholder ?? t('common.label.search.by', { field: placeholderText });
  const resolvedEmptyMessage = emptyMessage ?? t('common.no.results.found');
  const visibleOptions = filteredOptions.slice(0, maxVisibleOptions);
  const hiddenOptionsCount = filteredOptions.length - visibleOptions.length;
  const overflowMessage =
    hiddenOptionsCount > 0 && getOverflowMessage ? getOverflowMessage(hiddenOptionsCount) : null;

  return (
    <div className="flex flex-nowrap w-fit max-w-full">
      <div className={`bg-muted rounded-l-full space-x-1 flex ${value.length > 0 ? 'p-1' : ''}`}>
        {value.map((option) => (
          <div
            key={option.id}
            className="py-1.5 px-3 bg-primary text-background cursor-pointer rounded-full text-sm font-light inline-flex items-center justify-center whitespace-nowrap"
          >
            {option.name}
            <XIcon
              className="ml-2 w-4 h-4 transition ease-in-out cursor-pointer text-background hover:text-destructive"
              onClick={() => handleRemoveTag(option.id)}
            />
          </div>
        ))}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            disabled={loading}
            role="combobox"
            aria-expanded={open}
            className={cn(
              'py-1 pl-3 pr-2 h-10 min-w-[8rem] justify-between bg-muted text-foreground/50 hover:bg-muted-foreground/20 hover:text-foreground text-sm font-light',
              value.length > 0 ? 'rounded-r-full rounded-l-none' : 'rounded-full'
            )}
          >
            <span className="whitespace-nowrap">
              {loading ? t('common.loading') : placeholderText}
            </span>
            {loading ? (
              <Loader2Icon className="ml-2 w-4 h-4 animate-spin" />
            ) : (
              <ChevronsUpDownIcon className="ml-2 w-4 h-4 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 min-w-[--radix-popper-anchor-width]" align="start">
          <Command>
            <CommandInput placeholder={resolvedSearchPlaceholder} disabled={loading} />
            <CommandList>
              {loading ? (
                <div className="flex justify-center items-center p-4 text-sm">
                  <Loader2Icon className="mr-2 w-4 h-4 animate-spin" />
                  {t('common.loading')}
                </div>
              ) : (
                <>
                  <CommandEmpty>{resolvedEmptyMessage}</CommandEmpty>
                  <CommandGroup>
                    {visibleOptions.map((option) => (
                      <CommandItem
                        key={option.id}
                        value={`${option.name} ${option.id}`}
                        onSelect={() => handleSelect(option)}
                        className="cursor-pointer"
                      >
                        <CheckIcon className="mr-2 w-4 h-4 opacity-0" />
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {option.name}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {overflowMessage && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">{overflowMessage}</div>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export { SearchableSelectTagInput };
